import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import loginBg from '@/assets/branding/login-bg.png';
import logo from '@/assets/branding/idea-tsg-logo-light.png';

/**
 * Lista CERRADA de motivos: código de la URL → clave de traducción.
 *
 * Es la misma lista que `MotivoError` en el backend
 * (`proyecto/backend/app/auth/callback_errors.py`). El parámetro `motivo` solo
 * se usa para ELEGIR un texto de los locales; nunca se pinta. Así un enlace
 * manipulado al portal no puede mostrar un mensaje ajeno (phishing) ni inyectar
 * marcado.
 */
const REASON_KEYS = {
  'acceso-denegado': 'accessDenied',
  'requiere-interaccion': 'interactionRequired',
  consentimiento: 'consentRequired',
  microsoft: 'microsoft',
  reintentos: 'retries',
  identidad: 'identity',
  proveedor: 'provider',
  interno: 'internal',
} as const;

type ReasonKey = (typeof REASON_KEYS)[keyof typeof REASON_KEYS] | 'generic';

export function reasonKey(motivo: string | null): ReasonKey {
  // `Object.hasOwn` y no `in`: "constructor" o "__proto__" no son motivos.
  if (motivo !== null && Object.hasOwn(REASON_KEYS, motivo)) {
    return REASON_KEYS[motivo as keyof typeof REASON_KEYS];
  }
  return 'generic';
}

/**
 * Página pública a la que redirige `/api/auth/callback` cuando no puede
 * completar el login. Pantalla completa, sin el layout con sesión (como `/login`).
 */
export function AccessError() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const reason = reasonKey(params.get('motivo'));

  // Enlace fijo al backend, que es quien habla con Entra ID (patrón BFF).
  const loginUrl = '/api/auth/login';

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-3 py-6 sm:px-4 sm:py-8"
      style={{ backgroundColor: '#0f172a' }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.35,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />

      <div className="relative z-10 w-full max-w-sm space-y-5 sm:max-w-md sm:space-y-6">
        <div className="flex justify-center">
          <img src={logo} alt="IDEA TSG" className="h-10 object-contain sm:h-12 md:h-16" />
        </div>

        <main
          aria-labelledby="access-error-title"
          className="space-y-5 rounded-xl bg-white p-6 shadow-2xl sm:rounded-2xl sm:p-8"
        >
          <div className="space-y-3">
            <h1 id="access-error-title" className="text-xl font-bold text-gray-900">
              {t('accessError.title')}
            </h1>
            <p role="alert" className="text-sm leading-relaxed text-gray-700">
              {t(`accessError.reasons.${reason}`)}
            </p>
          </div>

          <a
            href={loginUrl}
            className="flex w-full items-center justify-center rounded-lg bg-[#0063B1] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#00559A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0063B1] focus-visible:ring-offset-2"
          >
            {t('accessError.cta')}
          </a>

          <p className="text-center text-xs leading-relaxed text-gray-600">
            {t('accessError.help')}
          </p>
        </main>

        <p className="text-center text-xs font-medium text-white/70">{t('login.footer')}</p>
      </div>
    </div>
  );
}
